import { toolkitUrls, goalbookUrls } from '@/links'
export { getProfileDetail, getProfiles, updateAccountProfileId, getContentReviewProfiles, postGoalReviewStreaming, stripMarkdownFences }
import $ from 'jquery'

/**
 * Strips markdown code fences from content
 * @param {string} content - The content to clean
 * @returns {string} - Content with markdown fences removed
 */
function stripMarkdownFences(content) {
  let cleaned = content.trim()
  if (cleaned.startsWith('```json')) {
    cleaned = cleaned.slice(7)
  } else if (cleaned.startsWith('```')) {
    cleaned = cleaned.slice(3)
  }
  if (cleaned.endsWith('```')) {
    cleaned = cleaned.slice(0, -3)
  }
  return cleaned.trim()
}

// options can include:
// refetch: 1
function getProfileDetail(profileId, options = {}) {
  if (!profileId) {
    throw 'Profile ID not specified'
  }
  return $.ajax({
    url: `${toolkitUrls.contentProfilesEndpoint}/profile/${profileId}?${$.param(options)}`,
    method: 'GET',
    dataType: 'json'
  })
}

// options can include:
// refetch: 1
function getProfiles(options = {}) {
  return $.ajax({
    url: `${toolkitUrls.contentProfilesEndpoint}/profiles?${$.param(options)}`,
    method: 'GET',
    dataType: 'json'
  })
}

function updateAccountProfileId({ userUuid, profileId }) {
  return $.ajax({
    url: `${toolkitUrls.contentProfilesEndpoint}/update_account`,
    method: 'PUT',
    contentType: 'json',
    data: JSON.stringify({ user_uuid: userUuid, content_profile_id: profileId }),
    xhrFields: { withCredentials: true }
  })
}
function getContentReviewProfiles() {
  return fetch(`${goalbookUrls.pathwaysApi}/content_review/profiles`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  }).then(response => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  })
}

/**
 * Streaming version of postGoalReview that handles Server-Sent Events
 * @param {Object} params - The review parameters
 * @param {string} params.goalText - The goal text to review
 * @param {Array<string>} params.objectiveTexts - Array of objective texts
 * @param {string} params.contentProfile - The content profile ID
 * @param {AbortSignal} params.signal - Optional AbortSignal to cancel the request
 * @param {boolean} params.betaMode - Whether to use beta mode
 * @param {Function} params.onProgress - Callback for progress updates with partial content
 * @returns {Promise<Object>} - The complete review result
 */
async function postGoalReviewStreaming({ goalText, goalUuid, objectiveTexts, contentProfile, signal, betaMode, onProgress }) {
  const response = await fetch(`${goalbookUrls.pathwaysApi}/content_review/review_goal_stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      goal_text: goalText,
      goal_uuid: goalUuid,
      objective_texts: objectiveTexts,
      content_profile: contentProfile,
      beta_mode: betaMode
    }),
    credentials: 'include',
    signal: signal
  })

  if (!response.ok || !response.body) {
    console.error('[STREAM] HTTP error:', response.status, response.statusText)
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullContent = ''
  let chunkCount = 0
  let lineCount = 0

  try {
    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        break
      }

      chunkCount++
      const decodedChunk = decoder.decode(value, { stream: true })

      buffer += decodedChunk
      const lines = buffer.split('\n')
      buffer = lines.pop() || '' // Keep incomplete line in buffer

      for (const line of lines) {
        lineCount++

        // Handle both SSE format (data: {...}) and plain JSON objects ({...})
        let dataStr = line
        if (line.startsWith('data: ')) {
          dataStr = line.slice(6)

          // Skip empty data lines
          if (!dataStr.trim()) {
            continue
          }

          try {
            const data = JSON.parse(dataStr)

            if (data.error) {
              console.error('[STREAM] Error in stream data:', data.error)
              throw new Error(data.error)
            }

            if (data.done) {
              // Strip markdown code fences if present
              const cleanedContent = stripMarkdownFences(fullContent)

              // Parse the complete accumulated JSON and return final result
              try {
                const review = JSON.parse(cleanedContent)
                return review
              } catch (parseError) {
                console.error('[STREAM] Failed to parse final JSON:', cleanedContent)
                throw parseError
              }
            }

            if (data.content) {
              fullContent += data.content

              // Call progress callback with accumulated content
              if (onProgress && typeof onProgress === 'function') {
                onProgress(fullContent)
              }
            }
          } catch (parseError) {
            console.error(`[STREAM] Error parsing SSE data on line ${lineCount}:`, parseError)
            console.error('[STREAM] Problematic line:', dataStr)
            // Don't throw here - continue processing other lines
          }
        } else if (line.trim()) {
          continue
        }
      }
    }

    // If we exit the loop without getting a done signal, try to parse what we have
    if (fullContent) {
      // Strip markdown code fences if present
      const cleanedContent = stripMarkdownFences(fullContent)


      try {
        return JSON.parse(cleanedContent)
      } catch (parseError) {
        console.error('[STREAM] Failed to parse accumulated JSON:', cleanedContent)
        throw parseError
      }
    }

    console.error('[STREAM] No content accumulated')
    throw new Error('Stream ended without completion signal')
  } catch (error) {
    // Don't log abort errors as fatal - they're expected when user cancels
    if (error.name === 'AbortError') {
      console.log('[STREAM] Request was aborted')
      throw error
    }

    console.error('[STREAM] Fatal error in streaming review:', error)
    console.error('[STREAM] Error stack:', error.stack)
    console.error('[STREAM] State at error:', {
      chunkCount,
      lineCount,
      bufferLength: buffer.length,
      fullContentLength: fullContent.length
    })
    throw error
  }
}
